import { motion } from 'framer-motion';

export const slideVariants = {
  enter: { x: 100, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -100, opacity: 0 }
};

export function CardSelector({ questionId, title, options, value, onSelect, step, setStep }) {
  return (
    <motion.div key={questionId} variants={slideVariants} initial="enter" animate="center" exit="exit">
      <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
        {options.map(o => (
          <button key={o.val} data-testid={`${questionId}-${o.val}`}
            onClick={() => { onSelect(o.val); setTimeout(() => setStep(step + 1), 400); }}
            className={`p-6 rounded-2xl border text-center transition-all ${value === o.val ? 'bg-gradient-to-br from-violet-600/30 to-cyan-500/30 border-cyan-500/50 scale-105' : 'bg-white/5 border-white/10 hover:border-white/30 hover:scale-102'}`}>
            <div className="text-3xl mb-2">{o.icon}</div>
            <div className="text-white font-medium">{o.label}</div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

export function SliderQuestion({ value, onChange, next, canGoNext }) {
  const color = value <= 3 ? 'text-red-400' : value <= 6 ? 'text-amber-400' : 'text-green-400';
  const label = value <= 3 ? 'Not yet useful' : value <= 6 ? 'Somewhat useful' : value <= 8 ? 'Very useful' : 'Cannot imagine without it';
  return (
    <motion.div key="value_score" variants={slideVariants} initial="enter" animate="center" exit="exit">
      <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>How valuable is SendPI to you right now?</h2>
      <div className={`text-8xl font-bold mb-4 ${color} transition-colors`} style={{ fontFamily: 'Space Grotesk' }}>{value}</div>
      <input data-testid="value-slider" type="range" min="1" max="10" value={value}
        onChange={(e) => onChange(parseInt(e.target.value))} className="w-full max-w-md mx-auto block accent-cyan-500 h-2" />
      <p className="text-white/50 mt-4">{label}</p>
      <button onClick={next} disabled={!canGoNext()} className="mt-8 px-8 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl font-semibold disabled:opacity-30">Continue</button>
    </motion.div>
  );
}

export function MultiSelectQuestion({ title, subtitle, features, selected, onToggle, next, canGoNext }) {
  return (
    <motion.div key="features_used" variants={slideVariants} initial="enter" animate="center" exit="exit">
      <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      {subtitle && <p className="text-white/40 mb-8">{subtitle}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl mx-auto">
        {features.map((f, i) => (
          <motion.button key={f.val} data-testid={`feature-${f.val}`}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            onClick={() => onToggle(f.val)}
            className={`p-4 rounded-xl border text-center transition-all ${selected.includes(f.val) ? 'bg-violet-600/20 border-violet-500/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
            <div className="text-2xl mb-1">{f.icon}</div>
            <div className="text-sm text-white">{f.label}</div>
          </motion.button>
        ))}
      </div>
      <button onClick={next} disabled={!canGoNext()} className="mt-8 px-8 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl font-semibold disabled:opacity-30">Continue</button>
    </motion.div>
  );
}

export function TextAreaQuestion({ questionId, title, subtitle, placeholder, value, onChange, next, canGoNext, minLength, borderColor = 'border-white/10', focusBorder = 'focus:border-cyan-500/50' }) {
  return (
    <motion.div key={questionId} variants={slideVariants} initial="enter" animate="center" exit="exit" className="max-w-xl mx-auto w-full">
      <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      {subtitle && <p className={`text-sm mb-8 ${questionId === 'frustrations' ? 'text-red-400/80' : 'text-white/40'}`}>{subtitle}</p>}
      <textarea data-testid={`${questionId}-input`} value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} rows={questionId === 'additional_comments' ? 5 : 4}
        className={`w-full bg-white/5 border ${borderColor} rounded-xl p-4 text-white resize-none focus:outline-none ${focusBorder}`} />
      {minLength && <p className="text-right text-white/30 text-sm mt-1">{(value || '').length} / {minLength} min</p>}
      <div className="flex gap-3 mt-6">
        <button onClick={next} disabled={!canGoNext()} className="px-8 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl font-semibold disabled:opacity-30">
          {!value && questionId === 'missing_feature' ? 'Skip' : 'Continue'}
        </button>
      </div>
    </motion.div>
  );
}

export function StackedOptions({ questionId, title, subtitle, options, value, onSelect, step, setStep }) {
  return (
    <motion.div key={questionId} variants={slideVariants} initial="enter" animate="center" exit="exit" className="max-w-lg mx-auto w-full">
      <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      {subtitle && <p className="text-white/40 mb-8">{subtitle}</p>}
      <div className="space-y-3">
        {options.map((o, i) => (
          <motion.button key={o.val} data-testid={`${questionId}-${o.val}`}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
            onClick={() => { onSelect(o.val); setTimeout(() => setStep(step + 1), 400); }}
            className={`w-full p-4 rounded-xl border text-left transition-all ${value === o.val ? `bg-gradient-to-r ${o.color} scale-[1.02]` : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
            <span className="text-white">{o.label}</span>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

export function SubmitQuestion({ value, onChange, handleSubmit, submitting }) {
  return (
    <motion.div key="additional_comments" variants={slideVariants} initial="enter" animate="center" exit="exit" className="max-w-xl mx-auto w-full">
      <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Last thing — anything else to tell us?</h2>
      <p className="text-white/40 text-sm mb-8">This feedback goes directly to the founder. Every response is read.</p>
      <textarea data-testid="final-comments-input" value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder="Bugs, ideas, dreams, complaints — we read every single one." rows={5}
        className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white resize-none focus:outline-none focus:border-cyan-500/50" />
      <div className="flex gap-3 mt-6">
        <button data-testid="feedback-submit-btn" onClick={handleSubmit} disabled={submitting}
          className="px-8 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50">
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
        {!value && (
          <button onClick={handleSubmit} disabled={submitting} className="px-6 py-3 bg-white/10 text-white/60 rounded-xl hover:bg-white/20">Skip & Submit</button>
        )}
      </div>
    </motion.div>
  );
}
